import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Droplets, Zap, Leaf, Award, Download, Play } from "lucide-react";
import { useState } from "react";

const Sustainability = () => {
  const [householdSize, setHouseholdSize] = useState(4);
  const [flushesPerDay, setFlushesPerDay] = useState(20);

  // Traditional toilet: 3.5 gallons per flush avg
  // Smart toilet: 0.9 gallons per flush avg
  const traditionalWaterUse = householdSize * flushesPerDay * 3.5 * 365;
  const smartWaterUse = householdSize * flushesPerDay * 0.9 * 365;
  const waterSaved = traditionalWaterUse - smartWaterUse;
  const co2Reduced = Math.round(waterSaved * 0.002); // Approx 0.002 lbs CO2 per gallon
  const treesSaved = Math.round((householdSize * 100 * 0.9) / 10); // TP reduction
  

  return (
    <>
      <SEO
        title="Sustainability & Water Conservation | Aureum VII Trading"
        description="Save 20,000+ gallons of water per year with eco-friendly smart toilets. WaterSense certified, energy-efficient luxury bathroom solutions for sustainable homes."
        keywords="water saving toilets, energy efficient bidet, sustainable bathroom, eco friendly smart toilet, water conservation, green bathroom fixtures"
        type="website"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary/5 via-background to-secondary/5">
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
                <Leaf className="w-4 h-4" />
                <span className="text-sm font-medium">Environmental Responsibility</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Redefining Luxury Through Sustainability
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Save 20,000+ gallons of water per year while experiencing unmatched luxury. 
                Our intelligent bathroom systems combine cutting-edge technology with environmental responsibility.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button asChild size="lg" className="text-lg px-8">
                  <a href="#calculator">Calculate Your Impact</a>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-lg px-8">
                  <LocalizedLink to="/smart-toilets">Eco-Luxury Models</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Eco Stats Banner */}
        <section className="py-12 bg-primary/5 border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">67%</div>
                <p className="text-sm text-muted-foreground">Less Water Than Traditional Toilets</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">20,000+</div>
                <p className="text-sm text-muted-foreground">Gallons Saved Per Year</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">90%</div>
                <p className="text-sm text-muted-foreground">Reduction in Toilet Paper Use</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">60%</div>
                <p className="text-sm text-muted-foreground">Less Energy Consumption</p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Environmental Commitment */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Environmental Commitment</h2>
              <p className="text-lg text-muted-foreground">
                At Aureum VII Trading, sustainability is not just a feature—it is the foundation of everything we do. 
                We are committed to creating luxury bathroom solutions that protect our planet's most precious resource: water.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Droplets className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Water Conservation</h3>
                <p className="text-muted-foreground">
                  Our smart toilets use just 0.8-1.2 gallons per flush, compared to 3-7 gallons for traditional models.
                </p>
              </Card>

              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Energy Efficiency</h3>
                <p className="text-muted-foreground">
                  Instant tankless heating technology reduces energy consumption by 60% compared to traditional systems.
                </p>
              </Card>

              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Leaf className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Waste Reduction</h3>
                <p className="text-muted-foreground">
                  Reduce toilet paper consumption by 90%, saving trees and eliminating landfill waste.
                </p>
              </Card>

              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Certified Green</h3>
                <p className="text-muted-foreground">
                  WaterSense, CE, and ISO 14001 certified for environmental responsibility and quality.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Environmental Impact Calculator */}
        <section id="calculator" className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Calculate Your Environmental Impact</h2>
                <p className="text-lg text-muted-foreground">
                  See how much water, energy, and money you can save by switching to an Aureum VII smart toilet.
                </p>
              </div>

              <Card className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <label className="block text-sm font-medium mb-2">Household Size</label>
                    <input
                      type="range"
                      min="1"
                      max="8"
                      value={householdSize}
                      onChange={(e) => setHouseholdSize(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-center mt-2 text-2xl font-bold text-primary">{householdSize} people</div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Flushes Per Day (per person)</label>
                    <input
                      type="range"
                      min="3"
                      max="8"
                      value={flushesPerDay / householdSize}
                      onChange={(e) => setFlushesPerDay(Number(e.target.value) * householdSize)}
                      className="w-full"
                    />
                    <div className="text-center mt-2 text-2xl font-bold text-primary">
                      {Math.round(flushesPerDay / householdSize)} times
                    </div>
                  </div>
                </div>

                <div className="border-t pt-8">
                  <h3 className="text-2xl font-bold mb-6 text-center">Your Annual Impact with Aureum VII:</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-primary/10 rounded-lg p-6 text-center">
                      <Droplets className="w-12 h-12 text-primary mx-auto mb-3" />
                      <div className="text-3xl font-bold text-primary mb-2">
                        {waterSaved.toLocaleString()}
                      </div>
                      <p className="text-sm text-muted-foreground">Gallons of Water Saved</p>
                    </div>

                    <div className="bg-primary/10 rounded-lg p-6 text-center">
                      <Leaf className="w-12 h-12 text-primary mx-auto mb-3" />
                      <div className="text-3xl font-bold text-primary mb-2">
                        {co2Reduced.toLocaleString()} lbs
                      </div>
                      <p className="text-sm text-muted-foreground">CO₂ Emissions Reduced</p>
                    </div>

                    <div className="bg-primary/10 rounded-lg p-6 text-center">
                      <Award className="w-12 h-12 text-primary mx-auto mb-3" />
                      <div className="text-3xl font-bold text-primary mb-2">
                        {treesSaved}
                      </div>
                      <p className="text-sm text-muted-foreground">Trees Saved (TP Reduction)</p>
                    </div>

                    <div className="bg-primary/10 rounded-lg p-6 text-center">
                      <Zap className="w-12 h-12 text-primary mx-auto mb-3" />
                      <div className="text-3xl font-bold text-primary mb-2">
                        Usage-dependent
                      </div>
                      <p className="text-sm text-muted-foreground">Money Saved on Water Bills</p>
                    </div>
                  </div>

                  <div className="mt-8 text-center">
                    <Button asChild size="lg">
                      <LocalizedLink to="/smart-toilets">Shop Eco-Luxury Models</LocalizedLink>
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Certifications & Standards */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Certifications & Standards</h2>
              <p className="text-lg text-muted-foreground">
                Our commitment to sustainability is backed by rigorous testing and internationally recognized certifications.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <Card className="p-8 text-center">
                <div className="text-6xl mb-4">💧</div>
                <h3 className="text-xl font-semibold mb-3">WaterSense Certified</h3>
                <p className="text-muted-foreground">
                  EPA WaterSense partnership label, certifying at least 20% greater water efficiency than federal standards.
                </p>
              </Card>

              <Card className="p-8 text-center">
                <div className="text-6xl mb-4">🌍</div>
                <h3 className="text-xl font-semibold mb-3">CE Marking</h3>
                <p className="text-muted-foreground">
                  European conformity certification for health, safety, and environmental protection standards.
                </p>
              </Card>

              <Card className="p-8 text-center">
                <div className="text-6xl mb-4">♻️</div>
                <h3 className="text-xl font-semibold mb-3">ISO 14001</h3>
                <p className="text-muted-foreground">
                  Environmental Management Systems certification for sustainable manufacturing and operations.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Resources Section */}
        <section className="py-20 bg-gradient-to-br from-secondary/5 to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Sustainability Resources</h2>
              <p className="text-lg text-muted-foreground">
                Download our comprehensive guides and technical documentation for architects, developers, and homeowners.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Sustainability Specification Guide</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Detailed technical specifications for water efficiency, energy consumption, and environmental certifications.
                    </p>
                    <Button variant="outline" size="sm">Download PDF</Button>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">LEED Credit Documentation</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Complete documentation package for earning LEED WE (Water Efficiency) credits in green building projects.
                    </p>
                    <Button variant="outline" size="sm">Download PDF</Button>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Water Conservation Technology Video</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Watch how our tankless instant heating and dual-flush technology delivers unmatched water savings.
                    </p>
                    <Button variant="outline" size="sm">Watch Video</Button>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Environmental Impact White Paper</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Research report on the lifecycle environmental cost of traditional vs. smart toilet technology.
                    </p>
                    <Button variant="outline" size="sm">Download PDF</Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Make a Difference?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
              Join thousands of eco-conscious homeowners, hotels, and developers who are saving water, 
              reducing waste, and protecting our planet—without sacrificing luxury.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8">
                <LocalizedLink to="/smart-toilets">Explore Eco-Luxury Models</LocalizedLink>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <LocalizedLink to="/contact">Request a Quote</LocalizedLink>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Sustainability;